/**
 * Performance Reporter — Lightweight Web Vitals monitoring
 *
 * Uses native PerformanceObserver API to track Core Web Vitals.
 * Reports to structured logger for observability.
 * Zero dependencies.
 *
 * Tracked metrics:
 * - LCP (Largest Contentful Paint) — loading performance
 * - FID/INP (First Input Delay / Interaction to Next Paint) — interactivity
 * - CLS (Cumulative Layout Shift) — visual stability
 * - TTFB (Time to First Byte) — server response
 *
 * Usage: call once at app mount after React hydration/render.
 *   Example: initPerformanceReporter();
 */
import { logger } from './logger';

let initialized = false;

// Web Vitals (LCP/CLS/TTFB) are informational, not actionable warnings — keep them at
// debug level in production to reduce console noise, info level elsewhere.
const isProduction =
  (typeof import.meta !== 'undefined' && import.meta.env?.PROD) ||
  (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production');
const vitalsLogLevel: 'debug' | 'info' = isProduction ? 'debug' : 'info';

export function initPerformanceReporter(): void {
  if (initialized) return;
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;
  initialized = true;

  logger.info('[App] Version', {
    version: __APP_VERSION__,
    buildTime: __BUILD_TIME__,
  });

  // LCP — Largest Contentful Paint
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
      if (lastEntry) {
        logger[vitalsLogLevel]('[Perf] LCP', { value: Math.round(lastEntry.startTime), unit: 'ms' });
      }
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {
    /* Not supported */
  }

  // CLS — Cumulative Layout Shift
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const shift = entry as PerformanceEntry & { hadRecentInput: boolean; value: number };
        if (!shift.hadRecentInput) {
          clsValue += shift.value;
        }
      }
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });

    // Report final CLS on page hide
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'hidden') {
          logger[vitalsLogLevel]('[Perf] CLS', { value: clsValue.toFixed(4) });
        }
      },
      { once: true }
    );
  } catch {
    /* Not supported */
  }

  // INP — Interaction to Next Paint (replaces FID)
  // Only warn for real input interactions (clicks/key presses), not hover/pointer
  // tracking events (pointerover/out/leave, mouseover/out) which fire constantly.
  try {
    const WARN_INTERACTION_TYPES = new Set(['click', 'keydown', 'pointerdown', 'pointerup']);
    const SLOW_INTERACTION_THRESHOLD_MS = 500;
    const WARN_DEDUPE_WINDOW_MS = 30000;
    const lastWarnedAt = new Map<string, number>();

    const inpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const interaction = entry as PerformanceEntry & { duration: number; name: string };
        if (!WARN_INTERACTION_TYPES.has(interaction.name)) continue;
        if (interaction.duration <= SLOW_INTERACTION_THRESHOLD_MS) continue;

        const now = Date.now();
        const lastWarned = lastWarnedAt.get(interaction.name);
        if (lastWarned !== undefined && now - lastWarned < WARN_DEDUPE_WINDOW_MS) continue;

        lastWarnedAt.set(interaction.name, now);
        logger.warn('[Perf] Slow interaction', {
          type: interaction.name,
          duration: Math.round(interaction.duration),
          unit: 'ms',
        });
      }
    });
    inpObserver.observe({ type: 'event', buffered: true });
  } catch {
    /* Not supported */
  }

  // TTFB — Time to First Byte
  try {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navEntry) {
      const ttfb = Math.round(navEntry.responseStart - navEntry.requestStart);
      logger[vitalsLogLevel]('[Perf] TTFB', { value: ttfb, unit: 'ms' });
    }
  } catch {
    /* Not supported */
  }
}
